/* eslint-disable react-hooks/preserve-manual-memoization */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { FC, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Space } from 'antd';
import { AxiosResponse } from 'axios';
import { matchSorter } from 'match-sorter';
import { TbArchive, TbChevronRight, TbSquareRoundedChevronLeftFilled, TbTrash } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useLifecycleList } from '@/queries/tanstack/database/entities';
import { getWorkspaceResourceDetailRoute, SmallTag, SmallTooltip, TickerExchangeTag } from '@/shared';
import { groupCompanySearchResults } from '@/shared/components/company-search/utils';
import WorkspaceResourceAvatar from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';
import { hasFeatureAddOnAccess } from '@/shared/entitlements';
import {
    useConnectedAccountStore,
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
    useTeamStore,
} from '@/stores';
import { useBillingStore } from '@/stores/database/aliases/billing';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';
import { Command, SetStateMethod } from '@/types';
import { ellipsisStyling } from '@/utilities';

import { resolveSearchPreferences, SearchPreferences } from '../../../../03-Content/routes/04-Search/preferences';
import CommandPaletteTabs from './00-CommandPaletteTabs';
import {
    applyCommandPaletteTabPreferences,
    COMMAND_PALETTE_ALL_TAB_KEY,
    commandPalettePlatformTabs,
    CommandPalettePreferences,
    getCommandPaletteSectionPreferences,
    isCommandPaletteSearchableProviderGroup,
    providerGroupForConnectedAccountType,
} from './00-CommandPaletteTabs/preferences';
import { orderCommandsBySource } from './commandOrdering';
import CommandPaletteResults from './components/CommandPaletteResults';
import { isCommandPaletteDataSourceGroup } from './constants/dataSources';
import { useCommandPaletteIcons } from './hooks/useCommandPaletteIcons';
import type { CommandPaletteResourceContextState } from './hooks/useCommandPaletteResourceContext';

interface ContentProps {
    globalCommands: Command[] | any;
    resourceContextState: CommandPaletteResourceContextState;
    selectedIndex: number;
    setSelectedIndex: SetStateMethod<number>;
    query: string;
    setQuery: SetStateMethod<string>;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    inputRef: RefObject<HTMLInputElement>;
}

const Content: FC<ContentProps> = ({
    open,
    globalCommands,
    resourceContextState,
    query,
    setQuery,
    setOpen,
    selectedIndex,
    setSelectedIndex,
    inputRef,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const { token } = useAntDesignTheme();

    const [results, setResults] = useState<Record<string, any>>({});
    const [activeTabKey, setActiveTabKey] = useState(COMMAND_PALETTE_ALL_TAB_KEY);
    const { preferences } = useUserPreferences();
    const commandPalettePreferences = preferences?.commandPalette as CommandPalettePreferences | undefined;
    const searchPreferences = resolveSearchPreferences(preferences?.search as SearchPreferences | undefined);
    const groupRelatedListings = searchPreferences.results.groupRelatedListings;

    const { groupOrder, groupToIconMap, itemToMap } = useCommandPaletteIcons();

    const navigate = useNavigate();
    const {
        isResourceContextMode,
        resourceContext,
        resourceContextCommands,
        resourceContextDismissed,
        setResourceContextDismissed,
    } = resourceContextState;

    const { folders } = useFolderStore();
    const { investments } = useInvestmentStore();
    const billingSnapshot = useBillingStore((state) => state.snapshot);
    const lifecyclesEnabled = hasFeatureAddOnAccess(billingSnapshot, 'lifecycles');
    const lifecycleListQuery = useLifecycleList({ enabled: open && lifecyclesEnabled });
    const lifecycles = useMemo(() => lifecycleListQuery.data?.items ?? [], [lifecycleListQuery.data?.items]);
    const { memos } = useMemoStore();
    const { pipelines } = usePipelineStore();
    const { dashboards } = useDashboardStore();
    const { lists } = useListStore();
    const { teams } = useTeamStore();
    const { connectedAccounts, fetchConnectedAccounts } = useConnectedAccountStore();
    const searchResults = useMemo(
        () =>
            open && query.trim() && lifecyclesEnabled ? { ...results, lifecycles: lifecycles.slice(0, 10) } : results,
        [lifecycles, lifecyclesEnabled, open, query, results],
    );

    const handleClosePalette = () => {
        setOpen(false);

        setQuery('');
    };

    const itemToFunction = (resourceType: any, item: any) => {
        const map = {
            folders: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            investments: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            lifecycles: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            memos: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            pipelines: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            dashboards: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            lists: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            teams: () => {
                navigate(getWorkspaceResourceDetailRoute(resourceType, item?.id));

                handleClosePalette();
            },
            people: () => {
                navigate('/lists/people');

                handleClosePalette();
            },
            // Others
            companies: () => {
                navigate(`/search/companies/${item?.symbol}`);

                handleClosePalette();
            },
            googleDrive: () => {
                window.open(item?.webViewLink, '_blank');

                handleClosePalette();
            },
            dropbox: () => {
                window.open(item?.webViewLink, '_blank');

                handleClosePalette();
            },
            box: () => {
                window.open(item?.webViewLink, '_blank');

                handleClosePalette();
            },
            notion: () => {
                window.open(item?.webViewLink, '_blank');

                handleClosePalette();
            },
        } as any;

        if (map[resourceType]) {
            return map[resourceType];
        }

        if (isCommandPaletteDataSourceGroup(resourceType)) {
            return () => {
                if (item?.webViewLink) {
                    window.open(item.webViewLink, '_blank');
                }

                handleClosePalette();
            };
        }

        return undefined;
    };

    // Dynamic list items based on search results
    const dynamicSearchGroups = useMemo(() => {
        const resourceGroups: [string, any[]][] = Object.entries(searchResults || {}).filter(
            ([_, arr]) => Array.isArray(arr) && arr.length > 0,
        );

        return resourceGroups.map(([resourceType, items]) => {
            const matchingConnectedAccount = connectedAccounts.find(
                (account) => providerGroupForConnectedAccountType(account.type) === resourceType,
            );
            const preparedItems =
                resourceType === 'companies' && groupRelatedListings
                    ? groupCompanySearchResults(items).map((group) => ({
                          item: group.primary,
                          relatedListings: group.alternatives,
                      }))
                    : items.map((item) => ({ item, relatedListings: [] }));

            const commands = preparedItems.map(({ item, relatedListings }) => {
                const isCompanyTagVisible =
                    resourceType === 'companies' && item?.enrichment?.exchange && item?.enrichment?.symbol;
                const isPeopleTagVisible = resourceType === 'people' && item?.email;
                const relatedListingSearchTerms = relatedListings.flatMap((listing) => [
                    listing?.companyName,
                    listing?.name,
                    listing?.symbol,
                    listing?.enrichment?.companyName,
                    listing?.enrichment?.symbol,
                ]);
                const companyLabelSuffix =
                    resourceType === 'companies' && (isCompanyTagVisible || relatedListings.length > 0) ? (
                        <Space size={4}>
                            {isCompanyTagVisible ? (
                                <TickerExchangeTag
                                    companyName={item?.enrichment?.companyName ?? item?.companyName ?? item?.name}
                                    exchange={item?.enrichment?.exchange}
                                    symbol={item?.enrichment?.symbol}
                                    website={item?.enrichment?.website}
                                />
                            ) : null}
                            {relatedListings.length > 0 ? (
                                <SmallTag
                                    message={`+${relatedListings.length.toLocaleString()} ${
                                        relatedListings.length === 1 ? 'listing' : 'listings'
                                    }`}
                                />
                            ) : null}
                        </Space>
                    ) : undefined;

                return {
                    id: `${resourceType}-${item.id || item.symbol}`,
                    label: item.editedName || item.name || item.title || item.label,
                    labelSuffix:
                        companyLabelSuffix ??
                        (isPeopleTagVisible ? (
                            <SmallTag message={item.email} />
                        ) : item.isArchived || item.archived ? (
                            <TbArchive
                                style={{
                                    verticalAlign: '-0.125rem',
                                    marginLeft: '0.5rem',
                                    color: token.colorTextTertiary,
                                }}
                            />
                        ) : !!item.deletedAt ? (
                            <TbTrash
                                style={{
                                    verticalAlign: '-0.125rem',
                                    marginLeft: '0.5rem',
                                    color: token.colorTextTertiary,
                                }}
                            />
                        ) : undefined),
                    group: resourceType,
                    icon: itemToMap(resourceType, item),
                    type: 'command',
                    searchTerms: [
                        item.editedName,
                        item.name,
                        item.title,
                        item.label,
                        item.symbol,
                        item.enrichment?.companyName,
                        item.enrichment?.symbol,
                        ...relatedListingSearchTerms,
                    ],
                    dataSourceId: matchingConnectedAccount?.id,
                    onSelect: itemToFunction(resourceType, item),
                    externalLink: isCommandPaletteDataSourceGroup(resourceType) && !!item?.webViewLink,
                    metadata: relatedListings.length > 0 ? { relatedListings } : undefined,
                } as Command;
            });

            return [resourceType, commands] as const;
        });
    }, [connectedAccounts, groupRelatedListings, searchResults]);

    const commandPaletteDataSourceTabs = useMemo(
        () =>
            connectedAccounts
                .map((account) => {
                    const providerGroup = providerGroupForConnectedAccountType(account.type);

                    return {
                        connectedAccountId: account.id,
                        description: `Search ${account.nickname || account.type || 'this data source'} and run source-specific actions.`,
                        key: `dataSource:${account.id}`,
                        label: account.nickname || account.type || 'Data Source',
                        providerGroup,
                        section: 'dataSources' as const,
                        sourceType: account.type,
                    };
                })
                .filter((tab) => isCommandPaletteSearchableProviderGroup(tab.providerGroup)),
        [connectedAccounts],
    );

    const visibleCommandPaletteTabs = useMemo(
        () => [
            ...applyCommandPaletteTabPreferences(
                commandPalettePlatformTabs,
                getCommandPaletteSectionPreferences(commandPalettePreferences, 'platform'),
            ),
            ...applyCommandPaletteTabPreferences(
                commandPaletteDataSourceTabs,
                getCommandPaletteSectionPreferences(commandPalettePreferences, 'dataSources'),
            ),
        ],
        [commandPaletteDataSourceTabs, commandPalettePreferences],
    );

    const activeTab = visibleCommandPaletteTabs.find((tab) => tab.key === activeTabKey) ?? visibleCommandPaletteTabs[0];

    useEffect(() => {
        if (!activeTab) {
            setActiveTabKey(COMMAND_PALETTE_ALL_TAB_KEY);

            return;
        }

        if (activeTab.key !== activeTabKey) {
            setActiveTabKey(activeTab.key);
        }
    }, [activeTab?.key, activeTabKey]);

    const commandMatchesTab = (cmd: Command | any, tabKey: string) => {
        if (tabKey === COMMAND_PALETTE_ALL_TAB_KEY) {
            return true;
        }

        const tab = visibleCommandPaletteTabs.find((item) => item.key === tabKey);

        if (!tab) {
            return true;
        }

        if (tab.providerGroup) {
            return cmd.dataSourceId ? cmd.dataSourceId === tab.connectedAccountId : cmd.group === tab.providerGroup;
        }

        if (tab.groups?.length) {
            return tab.groups.includes(cmd.group);
        }

        return true;
    };

    const normalizedGlobalCommands = useMemo<Command[]>(
        () => (Array.isArray(globalCommands) ? globalCommands.filter(Boolean) : []),
        [globalCommands],
    );

    const scopedGlobalCommands = useMemo(() => {
        if (isResourceContextMode && query.trim() === '') {
            return [];
        }

        return normalizedGlobalCommands;
    }, [isResourceContextMode, normalizedGlobalCommands, query]);

    // Filter and group everything together
    const filtered = useMemo(() => {
        // Flatten dynamic search groups first
        const dynamicCommands = dynamicSearchGroups.flatMap(([_, cmds]) => cmds);

        // Combine all commands
        const allCommands: Command[] = [...resourceContextCommands, ...scopedGlobalCommands, ...dynamicCommands];

        // Fuzzy match
        const matched: Command[] = matchSorter(allCommands, query, {
            keys: ['label', 'group', 'searchTerms'],
        }).filter((cmd) => commandMatchesTab(cmd, activeTab?.key ?? COMMAND_PALETTE_ALL_TAB_KEY));

        // Group matched commands
        const grouped: Record<string, Command[]> = {};
        matched.forEach((cmd) => {
            if (!grouped[cmd.group]) {
                grouped[cmd.group] = [];
            }
            grouped[cmd.group].push(cmd);
        });

        if (grouped.resourceActions) {
            grouped.resourceActions = orderCommandsBySource(grouped.resourceActions, resourceContextCommands);
        }

        // Sort by group order
        const sortedGroups = groupOrder
            .map((group) => [group, grouped[group]] as const)
            .filter(([_, items]) => items && items.length > 0);

        return sortedGroups;
    }, [
        activeTab?.key,
        dynamicSearchGroups,
        query,
        resourceContextCommands,
        scopedGlobalCommands,
        visibleCommandPaletteTabs,
    ]);

    const countByTab = useMemo(() => {
        const dynamicCommands = dynamicSearchGroups.flatMap(([_, cmds]) => cmds);
        const allCommands: Command[] = [...resourceContextCommands, ...scopedGlobalCommands, ...dynamicCommands];
        const matched: Command[] = matchSorter(allCommands, query, { keys: ['label', 'group', 'searchTerms'] });

        return visibleCommandPaletteTabs.reduce<Record<string, number>>((acc, tab) => {
            acc[tab.key] = matched.filter((cmd) => commandMatchesTab(cmd, tab.key)).length;

            return acc;
        }, {});
    }, [dynamicSearchGroups, query, resourceContextCommands, scopedGlobalCommands, visibleCommandPaletteTabs]);

    // Render-ready flat list
    const flatList = filtered.flatMap(([group, cmds]) => [
        { type: 'group', label: group },
        ...cmds.map((c: any) => ({ type: 'command', ...c })),
    ]);

    // Utility to find next/prev index skipping groups
    function findNextSelectable(start: number, direction: 1 | -1): number {
        const len = flatList.length;
        let i = start;

        while (true) {
            i += direction;

            // Wrap around
            if (i >= len) {
                i = 0;
            }
            if (i < 0) {
                i = len - 1;
            }

            // If we come full circle, break to avoid infinite loop
            if (i === start) {
                break;
            }

            if (flatList[i]?.type === 'command' && !flatList[i]?.disabled) {
                return i;
            }
        }

        return start;
    }

    // Auto-focuses first item in command palette
    useEffect(() => {
        const firstCommandIndex = flatList.findIndex((i) => i.type === 'command');

        setSelectedIndex(firstCommandIndex === -1 ? 0 : firstCommandIndex);
    }, [query, flatList.length, open]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const handler = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((i) => findNextSelectable(i, 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((i) => findNextSelectable(i, -1));
            } else if (e.key === 'Enter') {
                const item = flatList[selectedIndex] as Command | any;
                if (item?.type === 'command' && !item?.disabled) {
                    item.onSelect();
                    setOpen(false);
                    setQuery('');
                    setSelectedIndex(0);
                }
            } else if (e.key === 'Escape') {
                setOpen(false);
                setQuery('');
                setSelectedIndex(0);
            }
        };

        window.addEventListener('keydown', handler);

        return () => window.removeEventListener('keydown', handler);
    }, [flatList, selectedIndex, open]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 100);
            setResourceContextDismissed(false);
        }
    }, [inputRef, open]);

    useEffect(() => {
        if (open && connectedAccounts.length === 0) {
            fetchConnectedAccounts();
        }
    }, [connectedAccounts.length, fetchConnectedAccounts, open]);

    // scroll logic on arrow key movement
    const itemRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

    useEffect(() => {
        const node = itemRefs.current.get(selectedIndex);

        if (node) {
            node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [selectedIndex]);

    useEffect(() => {
        itemRefs.current.clear();
    }, [globalCommands]);

    // Workspace Resource Search

    // This effect clears old results right when query changes
    useEffect(() => {
        if (query.trim() === '') {
            setResults({});

            setLoading(false);
        } else {
            // Clear old results immediately when user types a new query
            setResults({});

            setLoading(true);
        }
    }, [query]);

    const handleWorkspaceSearch = async () => {
        try {
            setResults({
                folders: folders?.slice(0, 10),
                investments: investments?.slice(0, 10),
                lifecycles: lifecycles.slice(0, 10),
                memos: memos?.slice(0, 10),
                pipelines: pipelines?.slice(0, 10),
                dashboards: dashboards?.slice(0, 10),
                lists: lists?.slice(0, 10),
                teams: teams?.slice(0, 10),
            });

            await authorizedAxiosInstance
                .post('/api/command-palette', { query })
                .then((res: AxiosResponse) => {
                    if (res?.status === 200) {
                        setResults({
                            ...res.data,
                            folders: folders?.slice(0, 10),
                            investments: investments?.slice(0, 10),
                            lifecycles: lifecycles.slice(0, 10),
                            memos: memos?.slice(0, 10),
                            pipelines: pipelines?.slice(0, 10),
                            dashboards: dashboards?.slice(0, 10),
                            lists: lists?.slice(0, 10),
                            teams: teams?.slice(0, 10),
                        });
                    }
                })
                .catch(() => undefined)
                .finally(() => {
                    setLoading(false);
                });
        } catch (error) {
            captureException(error);
        }
    };

    // Debounced API search
    const [debouncedCallback, cancel] = useDebounce(
        () => {
            if (query !== '') {
                handleWorkspaceSearch();
            } else {
                setLoading(false);
            }
        },
        250,
        [query],
    );

    useEffect(() => {
        debouncedCallback();

        return () => cancel();
    }, [debouncedCallback, cancel]);

    // const resultsLength = Object.values(results);
    // const resultsKeys = Object.keys(results);

    // const keyToListItemMap = (item: any, key: any) => {
    //     const map = {
    //         companies: <SearchResultListItem key={item?.id} objectKey={key} item={item} setOpen={setOpen} />,
    //         googleDrive: <GoogleDriveFileResultListItem key={item?.id} objectKey={key} item={item} setOpen={setOpen} />,
    // removed unsafe any cast:      } as any;

    //     return map[key] || <WorkspaceResourceListItem key={item?.id} objectKey={key} item={item} setOpen={setOpen} />;
    // };

    return (
        <>
            <CommandPaletteTabs
                activeKey={activeTab?.key ?? COMMAND_PALETTE_ALL_TAB_KEY}
                connectedAccounts={connectedAccounts}
                countByTab={countByTab}
                onChange={(key) => {
                    setActiveTabKey(key);
                    setSelectedIndex(0);
                }}
            />
            {resourceContext?.resource && !resourceContextDismissed && (
                <div
                    style={{
                        borderBottom: `1px solid ${token.colorBorder}`,
                        padding: '0.375rem 0.5rem',
                    }}
                >
                    <Space.Compact size='small' style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <SmallTooltip title='Search All Commands' placement='topLeft' padding='0.5rem 0'>
                            <Button
                                onClick={() => {
                                    setResourceContextDismissed(true);
                                    setActiveTabKey(COMMAND_PALETTE_ALL_TAB_KEY);
                                }}
                                size='small'
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    width: 28,
                                }}
                            >
                                <TbSquareRoundedChevronLeftFilled
                                    style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                                />
                            </Button>
                        </SmallTooltip>
                        <SmallTooltip
                            title={`Go to ${resourceContext.parentLabel}`}
                            placement='topLeft'
                            padding='0.5rem 0'
                        >
                            <Button
                                onClick={() => {
                                    navigate(resourceContext.parentRoute);
                                    handleClosePalette();
                                }}
                                size='small'
                                style={{
                                    alignItems: 'center',
                                    display: 'inline-flex',
                                    gap: 0,
                                }}
                            >
                                <WorkspaceResourceAvatar
                                    resource={resourceContext.resourceType}
                                    size={16}
                                    style={{ marginInlineEnd: '0.25rem' }}
                                />
                                <span>{resourceContext.parentLabel}</span>
                            </Button>
                        </SmallTooltip>
                        <Button
                            aria-hidden='true'
                            size='small'
                            tabIndex={-1}
                            style={{
                                alignItems: 'center',
                                cursor: 'default',
                                display: 'inline-flex',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                                width: 28,
                            }}
                        >
                            <TbChevronRight style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />
                        </Button>
                        <Button
                            onClick={() => {
                                navigate(resourceContext.route);
                                handleClosePalette();
                            }}
                            size='small'
                            style={{
                                borderBottomRightRadius: token.borderRadiusSM,
                                borderTopRightRadius: token.borderRadiusSM,
                                alignItems: 'center',
                                display: 'inline-flex',
                                gap: 0,
                                maxWidth: 16 * 20,
                            }}
                        >
                            {itemToMap(resourceContext.resourceType, resourceContext.resource, '0.25rem')}
                            <span style={{ ...ellipsisStyling, display: 'inline-block' }}>{resourceContext.title}</span>
                        </Button>
                    </Space.Compact>
                </div>
            )}
            <CommandPaletteResults
                flatList={flatList}
                groupToIconMap={groupToIconMap}
                itemRefs={itemRefs}
                loading={loading}
                query={query}
                selectedIndex={selectedIndex}
                setOpen={setOpen}
                setQuery={setQuery}
                setSelectedIndex={setSelectedIndex}
            />
        </>
    );
};

export default Content;
